import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GET_PROJECT_VISIBILITY_PER_MONITOR } from '../../../query/project/stats/keys';
import { ChatType } from '../../../../types/monitor/ChatType';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import { ProjectVisibilityPerMonitorPoint } from '../../../../models/projects/stats/AllBrandsStatsResponse';
import { getProjectVisibilityPerMonitor } from '../../../services/project/stats/ProjectDailyStatsService';

/**
 * Per-question (per-monitor) Visibility % for the selected brand — the same metric as the
 * visibility time-series, but averaged per question instead of bucketed by date. Used to
 * populate the Visibility column in the prompts table on the V2 project preview page.
 */
export const useGetProjectVisibilityPerMonitor = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const {
    data: visibilityPerMonitor,
    isLoading,
    isPlaceholderData,
    isError,
    error,
    refetch,
  } = useQuery<ProjectVisibilityPerMonitorPoint[]>({
    queryKey: [GET_PROJECT_VISIBILITY_PER_MONITOR, projectId, chatType, from, to, monitorPromptIds, filterChatTypes],
    queryFn: () => {
      return getProjectVisibilityPerMonitor(projectId!, chatType!, from, to, filterChatTypes, monitorPromptIds);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    visibilityPerMonitor: queryEnabled ? visibilityPerMonitor ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    // `keepPreviousData` keeps `isLoading` false after a key change (dates, filters) while the old
    // range is still on screen — `isLoading` alone makes the preview page look frozen, not loading.
    isRefreshing: queryEnabled ? isPlaceholderData : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () => {
      return queryClient.invalidateQueries({
        queryKey: [GET_PROJECT_VISIBILITY_PER_MONITOR, projectId, chatType, from, to, monitorPromptIds, filterChatTypes],
        refetchType: 'active',
      });
    },
  };
};
